import { createContext, useContext, useState, useEffect } from 'react'
import { useSelectionStore } from '@/state/selection.store'
import posthogAnalytics from '@/lib/posthog'
import { safeLocalStorage } from '@/lib/safe-storage'
import { ensureSharedAuthCookie } from '@/lib/sharedAuthCookie'

const API_BASE = import.meta.env.VITE_API_BASE_URL

const AuthContext = createContext()

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  // Check for existing auth on mount
  useEffect(() => {
    const token = safeLocalStorage.getItem('auth_token')
    const userData = safeLocalStorage.getItem('user_data')

    if (token && userData) {
      try {
        const parsed = JSON.parse(userData)
        setUser(parsed)

        // Identify user in PostHog
        if (parsed.id) {
          posthogAnalytics.identify(parsed.id, {
            name: parsed.name,
            email: parsed.email,
            role: parsed.role,
          })
        }

        // If email is missing, fetch from user profile API
        if (!parsed.email && parsed.id) {
          fetch(`${API_BASE}user/${parsed.id}/`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'x-access-token': token,
            },
          })
            .then(res => res.ok ? res.json() : null)
            .then(result => {
              const profile = result?.data || result
              if (profile?.email) {
                const updated = { ...parsed, email: profile.email, name: profile.name || parsed.name, isAdmin: profile.isAdmin || parsed.isAdmin || false }
                safeLocalStorage.setItem('user_data', JSON.stringify(updated))
                setUser(updated)
                
                // Update PostHog with complete user data
                posthogAnalytics.identify(updated.id, {
                  name: updated.name,
                  email: updated.email,
                  role: updated.role,
                })
              }
            })
            .catch(() => {}) // silently ignore
        }
      } catch (error) {
        // Clear invalid data
        safeLocalStorage.removeItem('auth_token')
        safeLocalStorage.removeItem('user_data')
      }
    } else if (token && !userData) {
      // Token exists but no user_data — try to silently recover.
      // Look up the userId from the uRI key (set during cookie-based auth).
      // If we can fetch the profile, rebuild user_data so the dashboard works.
      // If anything fails, just continue — never log the user out.
      const storedUserId = safeLocalStorage.getItem('uRI')
      if (storedUserId) {
        fetch(`${API_BASE}user/${storedUserId}/`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-access-token': token,
          },
        })
          .then(res => res.ok ? res.json() : null)
          .then(result => {
            if (!result) { setIsLoading(false); return }
            const profile = result?.data || result
            const recovered = {
              id: profile._id || profile.id || storedUserId,
              name: profile.name || 'User',
              email: profile.email || '',
              role: 'user',
              isAdmin: profile.isAdmin || false,
            }
            safeLocalStorage.setItem('user_data', JSON.stringify(recovered))
            setUser(recovered)
            setIsLoading(false)

            if (recovered.id) {
              posthogAnalytics.identify(recovered.id, {
                name: recovered.name,
                email: recovered.email,
                role: recovered.role,
              })
            }
          })
          .catch(() => { setIsLoading(false) }) // silently continue
        return // wait for recovery attempt before setting isLoading
      }
    }

    setIsLoading(false)
  }, [])

  const login = async (email, password) => {

    const response = await fetch(`${API_BASE}login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    })

    const result = await response.json()

    if (!response.ok || !result.data?.token) {
      const errorMessage = result.authFlag === 'emailNotVerified'
        ? 'Please verify your email before logging in'
        : result.authFlag === 'previouslyLoggedInWithMagicLink'
          ? 'Please use magic link to log in'
          : result.authFlag === 'invalidPassword'
            ? 'Invalid password'
            : result.message || 'Login failed'
      throw new Error(errorMessage)
    }

    const userData = {
      id: result.data._id,
      name: result.data.name,
      email: result.data.email,
      role: 'user',
      isAdmin: result.data.isAdmin || false,
    }

    safeLocalStorage.setItem('auth_token', result.data.token)
    safeLocalStorage.setItem('user_data', JSON.stringify(userData))
    setUser(userData)
    // This login form doesn't go through the accounts app, which is what normally
    // sets the shared cookies the builder needs.
    ensureSharedAuthCookie()

    // Identify user in PostHog after successful login
    posthogAnalytics.identify(userData.id, {
      name: userData.name,
      email: userData.email,
      role: userData.role,
    })

    // Fetch default workspace: { success: true, data: { _id, aiCredits, plans, ... } }
    const wsResp = await fetch(`${API_BASE}workspaces/default`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-access-token': result.data.token,
      },
    })

    const wsResult = await wsResp.json()
    console.log('Workspace Response:', wsResult)

    // Handle different response structures
    let workspaceId = null
    if (wsResult?.data?._id) {
      workspaceId = wsResult.data._id
    } else if (wsResult?._id) {
      workspaceId = wsResult._id
    } else if (wsResult?.data?.id) {
      workspaceId = wsResult.data.id
    } else if (wsResult?.id) {
      workspaceId = wsResult.id
    }

    console.log('Extracted workspaceId:', workspaceId)

    if (workspaceId) {
      useSelectionStore.getState().setActiveWorkspace(workspaceId)
    } else {
      console.warn('No workspace ID found in response, workspace may not be set')
    }

    return { success: true, user: userData, workspaceId }
  }

  const register = async (userData) => {
    const API_BASE = import.meta.env.VITE_API_BASE_URL

    const response = await fetch(`${API_BASE}register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: userData.name,
        username: userData.username,
        email: userData.email,
        password: userData.password
      })
    })

    const result = await response.json()

    if (!response.ok || !result.data) {
      const errorMessage = result.authFlag === 'accountAlreadyExists'
        ? 'An account with this email already exists'
        : result.authFlag === 'emailNotVerified'
          ? 'Please verify your email to complete registration'
          : result.message || 'Registration failed'
      throw new Error(errorMessage)
    }

    return { success: true, message: 'Registration successful' }
  }

  const logout = () => {
    // 1. Reset PostHog session
    posthogAnalytics.reset()

    // 2. Clear local storage tokens
    safeLocalStorage.removeItem('auth_token')
    safeLocalStorage.removeItem('user_data')
    setUser(null)

    // 3. Clear cookies — must specify each domain a cookie was set on
    //    Generic clear (covers cookies set without an explicit domain)
    document.cookie.split(';').forEach((c) => {
      const name = c.split('=')[0].trim()
      if (name) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`
      }
    })
    //    Explicit clear of auth cookies on ALL known ecosystem domains.
    //    A cookie set with domain=X can ONLY be deleted by specifying domain=X.
    const authCookieNames = ['token', 'userId']
    const knownDomains = [
      '.codedesign.app',
      '.codedesign.ai',
      'dev.codedesign.app',
      '.dev.codedesign.app',
      'dev.codedesign.ai',
      '.dev.codedesign.ai',
      'staging-accounts.codedesign.app',
      'staging-production-jqesv.codedesign.app',
      'accounts.codedesign.ai',
      '.cdsn.me',
      'dev.cdsn.me',
      '.dev.cdsn.me',
      'dashboard.codedesign.ai',
      '.dashboard.codedesign.ai',
      'dashboard.cdsn.me',
      '.dashboard.cdsn.me',
    ]
    authCookieNames.forEach((name) => {
      knownDomains.forEach((domain) => {
        document.cookie = `${name}=; Path=/; Domain=${domain}; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`
      })
    })

    // 4. Redirect to builder logout (which chains to accounts logout)
    const builderUrl = import.meta.env.VITE_BASE_BUILDER_URL
    if (builderUrl) {
      const logoutUrl = `${builderUrl.replace(/\/$/, '')}/logout`
      window.location.href = logoutUrl
    }
  }

  const isAuthenticated = () => {
    return !!user && !!safeLocalStorage.getItem('auth_token')
  }

  const value = {
    user,
    isLoading,
    login,
    register,
    logout,
    isAuthenticated
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    // Return safe defaults instead of throwing — prevents crashes during
    // error boundary recovery and Vite HMR where the provider may be temporarily lost
    return {
      user: null,
      isLoading: true,
      login: async () => { throw new Error('AuthProvider not available') },
      register: async () => { throw new Error('AuthProvider not available') },
      logout: () => {},
      isAuthenticated: () => false,
    }
  }
  return context
}